import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import type { CampaignSequenceDto } from '../../api/models/CampaignSequenceDto';
import type { CampaignSequenceEntryDto } from '../../api/models/CampaignSequenceEntryDto';
import type { CrudFormProps } from '../../components/CrudPage';
import { CrudPage } from '../../components/CrudPage';
import { CampaignSequenceEntryDetailDialog } from './CampaignSequenceEntryDetailDialog';
import { generateTempUUID } from '../../utils/uuid';

type CampaignSequenceWithId = CampaignSequenceDto & { id: string };
export type CampaignSequenceEntryWithId = CampaignSequenceEntryDto & { id: string };

interface CampaignSequenceFormDialogProps extends CrudFormProps<CampaignSequenceWithId> {}

export const CampaignSequenceFormDialog: React.FC<CampaignSequenceFormDialogProps> = ({
  value,
  onClose,
  onSave,
  isClone
}) => {
  const { t } = useTranslation(['common']);
  const [visible, setVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [currentValue, setCurrentValue] = useState<CampaignSequenceWithId>(() => {
    const initialValue = value || {
      uuid: undefined,
      dayOfWeek: undefined,
      name: '',
      isDefault: false,
      description: '',
      entries: []
    };
    return {
      ...initialValue,
      name: initialValue.name ?? '',
      isDefault: isClone ? false : (initialValue.isDefault ?? false),
      id: initialValue.uuid || ''
    };
  });
  const [entriesRefreshKey, setEntriesRefreshKey] = useState(0);
  const [entries, setEntries] = useState<CampaignSequenceEntryDto[]>([]);

  const weekdayOptions = [
    { value: 'MONDAY', text: t('weekday.MONDAY') },
    { value: 'TUESDAY', text: t('weekday.TUESDAY') },
    { value: 'WEDNESDAY', text: t('weekday.WEDNESDAY') },
    { value: 'THURSDAY', text: t('weekday.THURSDAY') },
    { value: 'FRIDAY', text: t('weekday.FRIDAY') },
    { value: 'SATURDAY', text: t('weekday.SATURDAY') },
    { value: 'SUNDAY', text: t('weekday.SUNDAY') }
  ];

  const schema = z.object({
    dayOfWeek: z.string().min(1, t('validation.required')),
    name: z.string().min(1, t('validation.required')),
    description: z.string().optional(),
    isDefault: z.boolean().optional()
  });

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      dayOfWeek: currentValue.dayOfWeek || '',
      name: currentValue.name || '',
      description: currentValue.description || '',
      isDefault: currentValue.isDefault ?? false
    }
  });

  // Initialize entries state when form data changes
  useEffect(() => {
    setEntries(currentValue.entries || []);
    setEntriesRefreshKey(prev => prev + 1);
  }, [currentValue.entries]);

  useEffect(() => {
    setVisible(true);
  }, []);

  const handleClose = () => {
    setSaveError(null);
    setVisible(false);
    setTimeout(() => onClose(), 300);
  };

  /** Validates that for each product with multiple entries, the last occurrence has maxBatches = null (unlimited). */
  const validateLastOccurrenceUnlimited = useCallback((entries: CampaignSequenceEntryDto[]): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (!entries?.length) return { isValid: true, errors };

    const entriesByProduct = new Map<string, CampaignSequenceEntryDto[]>();
    entries.forEach(entry => {
      if (entry.productId) {
        const list = entriesByProduct.get(entry.productId) || [];
        list.push(entry);
        entriesByProduct.set(entry.productId, list);
      }
    });

    entriesByProduct.forEach((productEntries, productId) => {
      if (productEntries.length < 2) return;
      const sorted = [...productEntries].sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));
      const last = sorted[sorted.length - 1];
      if (last.maxBatches != null && last.maxBatches !== undefined) {
        const productName = last.productName || productId;
        errors.push(t('campaignSequence.validation.lastEntryMustBeUnlimited', 'Das letzte Vorkommen von Produkt "{{productName}}" muss unbegrenzt sein (Max Teig-Chargen = leer).', { productName }));
      }
    });

    return { isValid: errors.length === 0, errors };
  }, [t]);

  const handleFormSubmit = handleSubmit(async (formData) => {
    setSaveError(null);
    const dataToSave: CampaignSequenceWithId = {
      ...currentValue,
      ...formData,
      name: (formData.name as string)?.trim() || currentValue.name,
      isDefault: currentValue.isDefault ?? false,
      uuid: currentValue.uuid,
      id: currentValue.id || currentValue.uuid || ''
    };

    const entries = dataToSave.entries ?? [];
    const validation = validateLastOccurrenceUnlimited(entries);
    if (!validation.isValid) {
      setSaveError(validation.errors.join('\n'));
      return;
    }

    try {
      setIsSaving(true);
      await Promise.resolve(onSave(dataToSave));
      handleClose();
    } catch (err: unknown) {
      const message =
        (err as { body?: { message?: string } })?.body?.message ||
        (err as Error)?.message ||
        t('campaignSequence.validation.saveErrorGeneric', 'Die Sequenz konnte nicht gespeichert werden. Bitte prüfen Sie die Einträge.');
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  });

  // Handler for the toolbar 'Add' action - always inserts at the end
  const handleEntriesAdd = useCallback((_selectedItems: CampaignSequenceEntryWithId[]): CampaignSequenceEntryWithId => {
    const currentEntries = currentValue.entries || [];
    const newSequenceNumber = currentEntries.length + 1;
    
    const newEntry = {
      uuid: generateTempUUID(),
      productId: '',
      productName: '',
      sequenceNumber: newSequenceNumber,
      maxBatches: undefined
    } as CampaignSequenceEntryDto;
    
    return { ...newEntry, id: newEntry.uuid || generateTempUUID() };
  }, [currentValue.entries]);

  // Handler for drag & drop (priority change)
  const handleEntriesPriorityChange = useCallback(async (reorderedEntries: any[]) => {
    // Remove the id field and update sequenceNumber based on the index (1-based)
    const entriesWithoutId = reorderedEntries.map((entry, index) => {
      const { id, ...entryData } = entry;
      // Set sequenceNumber based on the new position (1-based)
      entryData.sequenceNumber = index + 1;
      return entryData;
    });
    
    // Update local state FIRST (this is used by fetchData immediately)
    setEntries(entriesWithoutId);
    // Then update form state
    setCurrentValue(prev => ({ ...prev, entries: entriesWithoutId }));
    // Force grid refresh by updating refreshKey
    setEntriesRefreshKey(prev => prev + 1);
  }, []);

  // Handler for delete
  const handleEntriesDelete = useCallback((selectedItems: any[]) => {
    if (selectedItems.length === 0) return;
    
    const uuidsToDelete = selectedItems.map(item => item.uuid || item.id);
    const filteredEntries = (currentValue.entries || []).filter(entry => !uuidsToDelete.includes(entry.uuid));
    
    // Re-sequence numbers
    const updatedEntries = filteredEntries.map((entry, index) => ({
      ...entry,
      sequenceNumber: index + 1
    }));
    
    setCurrentValue(prev => ({ ...prev, entries: updatedEntries }));
    setEntries(updatedEntries);
    setEntriesRefreshKey(prev => prev + 1);
  }, [currentValue.entries]);

  const formatMaxBatches = (data: CampaignSequenceEntryDto) => {
    return data.maxBatches !== null && data.maxBatches !== undefined 
      ? String(data.maxBatches) 
      : '-';
  };

  const footerTemplate = () => (
    <div className="flex flex-col gap-2 p-2">
      {saveError && (
        <div className="rounded border border-red-500 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {saveError}
        </div>
      )}
      <div className="flex justify-end gap-2">
      <ButtonComponent 
        onClick={handleClose} 
        cssClass="e-outline e-btn e-outline"
        type="button"
      >
        {t('actions.cancel')}
      </ButtonComponent>
      <ButtonComponent 
        onClick={handleFormSubmit} 
        cssClass="e-primary e-btn e-primary"
        type="button"
        disabled={isSaving}
      >
        {isSaving ? t('actions.saving') : t('actions.save')}
      </ButtonComponent>
      </div>
    </div>
  );

  return (
    <DialogComponent
      width="800px"
      height="700px"
      isModal={true}
      visible={visible}
      close={handleClose}
      header={value?.uuid ? t('campaignSequence.form.editTitle') : t('campaignSequence.form.createTitle')}
      showCloseIcon={true}
      cssClass="campaign-sequence-dialog"
      footerTemplate={footerTemplate}
    >
      <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="dayOfWeek">{t('campaignSequence.form.weekday')}*</label>
          <Controller
            name="dayOfWeek"
            control={control}
            render={({ field }) => (
              <DropDownListComponent
                id="dayOfWeek"
                dataSource={weekdayOptions}
                fields={{ text: 'text', value: 'value' }}
                value={field.value}
                change={(e: any) => {
                  field.onChange(e.value);
                  setCurrentValue(prev => ({ ...prev, dayOfWeek: e.value }));
                }}
                placeholder={t('campaignSequence.form.weekdayPlaceholder')}
                floatLabelType="Never"
              />
            )}
          />
          {errors.dayOfWeek && (
            <span style={{ color: 'red', fontSize: '12px' }}>
              {errors.dayOfWeek.message}
            </span>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="name">{t('campaignSequence.form.name')}*</label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextBoxComponent
                id="name"
                value={field.value}
                change={(e: any) => {
                  field.onChange(e.value);
                  setCurrentValue(prev => ({ ...prev, name: e.value }));
                }}
                placeholder={t('campaignSequence.form.namePlaceholder')}
                floatLabelType="Never"
              />
            )}
          />
          {errors.name && (
            <span style={{ color: 'red', fontSize: '12px' }}>
              {errors.name.message}
            </span>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <Controller
            name="isDefault"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <CheckBoxComponent
                  id="isDefault"
                  label={t('campaignSequence.form.isDefault')}
                  checked={field.value === true}
                  change={(e: any) => {
                    const checked = e.checked === true;
                    field.onChange(checked);
                    setCurrentValue(prev => ({ ...prev, isDefault: checked }));
                  }}
                />
              </div>
            )}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="description">{t('campaignSequence.form.description')}</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextBoxComponent
                id="description"
                value={field.value}
                change={(e: any) => {
                  field.onChange(e.value);
                  setCurrentValue(prev => ({ ...prev, description: e.value }));
                }}
                placeholder={t('campaignSequence.form.descriptionPlaceholder')}
                floatLabelType="Never"
                multiline={true}
              />
            )}
          />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* minHeight: 0 is needed so that the flex container may become smaller than its content and the grid can scroll inside */}
          <CrudPage<CampaignSequenceEntryWithId>
            key={`entries-${entriesRefreshKey}`}
            titleKey="campaignSequence.form.entries"
            showTitle={false}
            gridHeight="100%"
            localMode={true}
            gridMeta={[
              { 
                field: 'productName', 
                headerKey: 'campaignSequenceEntry.grid.field.materialName', 
                width: 250
              },
              { 
                field: 'maxBatches', 
                headerKey: 'campaignSequenceEntry.grid.field.maxBatches', 
                width: 150,
                render: formatMaxBatches
              }
            ]}
            fetchData={(): Promise<CampaignSequenceEntryWithId[]> => {
              const sourceEntries = entries.length > 0 ? entries : (currentValue.entries || []);

              // Sort by sequenceNumber
              const sortedEntries = [...sourceEntries].sort((a, b) => {
                const aNum = a.sequenceNumber || 0;
                const bNum = b.sequenceNumber || 0;
                return aNum - bNum;
              });
              
              const result = sortedEntries.map(entry => ({
                ...entry,
                id: entry.uuid || generateTempUUID()
              }));
              
              return Promise.resolve(result);
            }}
            saveData={async (entry: CampaignSequenceEntryWithId) => {
              // Remove the id field and use uuid for the actual data
              const { id, ...entryData } = entry;
              
              const currentEntries = currentValue.entries || [];
              const existingEntry = currentEntries.find(e => e.uuid === entryData.uuid);
              
              let updatedEntries: CampaignSequenceEntryDto[];
              
              if (existingEntry) {
                // Update existing entry
                updatedEntries = currentEntries.map(e => 
                  e.uuid === entryData.uuid ? entryData : e
                );
              } else {
                // Add new entry - sequenceNumber is set automatically based on the position
                const newEntry = {
                  ...entryData,
                  uuid: entryData.uuid || generateTempUUID(),
                  sequenceNumber: currentEntries.length + 1
                };
                updatedEntries = [...currentEntries, newEntry];
              }
              
              // Re-sequence the numbers based on the current position
              const resequencedEntries = updatedEntries.map((e, index) => ({
                ...e,
                sequenceNumber: index + 1
              }));
              
              setCurrentValue(prev => ({ ...prev, entries: resequencedEntries }));
              setEntries(resequencedEntries);
              setEntriesRefreshKey(prev => prev + 1);
            }}
            deleteData={async (id: string) => {
              const filteredEntries = (currentValue.entries || []).filter(e => e.uuid !== id);
              
              // Re-sequence numbers
              const resequencedEntries = filteredEntries.map((e, index) => ({
                ...e,
                sequenceNumber: index + 1
              }));
              
              setCurrentValue(prev => ({ ...prev, entries: resequencedEntries }));
              setEntries(resequencedEntries);
              setEntriesRefreshKey(prev => prev + 1);
            }}
            FormComponent={({ value, onClose, onSave }: any) => {
              // Map id back to uuid for CampaignSequenceEntryDetailDialog
              const entryWithUuid = value ? {
                ...value,
                uuid: value.id || value.uuid
              } : value;
              
              return (
                <CampaignSequenceEntryDetailDialog
                  value={entryWithUuid}
                  onClose={onClose}
                  onSave={(entryData: any) => {
                    // Map uuid back to id for CrudPage
                    const entryWithId = { ...entryData, id: entryData.uuid };
                    onSave(entryWithId);
                  }}
                />
              );
            }}
            showDefaultToolbar={true}
            showAddButton={true}
            showEditButton={true}
            showDeleteButton={true}
            showCloneButton={false}
            showSearchButton={false}
            allowFiltering={false}
            allowSorting={false}
            noPadding={true}
            showSummary={false}
            hasPriority={true}
            priorityField="sequenceNumber"
            onPriorityChange={handleEntriesPriorityChange}
            onAdd={handleEntriesAdd}
            onDelete={handleEntriesDelete}
          />
        </div>
      </div>
    </DialogComponent>
  );
};
